import { ref, watch } from 'vue'
import axiosInstance from '@/axios'

/**
 * useStorefrontNav -- fetch the storefront category nav bar for a store
 * (`GET /api/storefront/<slug>/nav`).
 *
 * Card: Live Storefront - Home/Category/Product (ADR 0060/0061)
 * https://trello.com/c/q7EQ3a0b -- pebble P5.
 *
 * The backend returns the home category FIRST then the VISIBLE categories in
 * nav_order, pre-filtered (hidden omitted). This composable stores that array
 * VERBATIM (no client sort/filter) for StorefrontCategoryNav to render.
 *
 * Public surface:
 *   const { nav, loading, error, fetchNav } = useStorefrontNav()
 *   nav:     ref<Array>  last successful payload: [{ name, slug, is_home }]
 *   loading: ref<boolean>
 *   error:   ref<{status,message}|null>
 *   fetchNav(slug): Promise<void>
 *
 * "Once per store" (plan P5 step 5): fetchNav consults a module-level cache
 * keyed by slug and skips the network round-trip on a cache hit, so a shopper
 * navigating Home -> Category -> Home fetches /nav ONCE. Concurrent/stale
 * fetches are de-duped via a generation counter (the same pattern used by
 * useStorefrontCategory). The cache is process-lived; a hard reload refetches.
 */

// Module-level cache so the nav is fetched once per store across the separate
// Home/Category route components (each remounts on navigation). Keyed by slug.
const navCache = new Map()

// Bumped by every clearStorefrontNavCache(). Each composable instance watches
// it (synchronously) to empty its own `nav` and discard any in-flight read, so
// a consumer that is ALREADY showing category names hides them on pass loss or
// logout instead of keeping a stale list on screen.
const cacheEpoch = ref(0)

export function useStorefrontNav() {
    const nav = ref([])
    const loading = ref(false)
    const error = ref(null)

    let generation = 0

    watch(
        cacheEpoch,
        () => {
            generation++
            nav.value = []
            loading.value = false
        },
        { flush: 'sync' }
    )

    const fetchNav = async (slug) => {
        if (slug === undefined || slug === null || slug === '') {
            error.value = { status: 0, message: 'Missing store identifier' }
            nav.value = []
            loading.value = false
            return
        }

        // EVERY call supersedes any earlier one, cache hit or not: a late
        // response for a previous slug must never overwrite this slug's view.
        const myGeneration = ++generation

        // Cache hit: serve without a network round-trip (fetch once per store).
        if (navCache.has(slug)) {
            nav.value = navCache.get(slug)
            error.value = null
            loading.value = false
            return
        }

        loading.value = true
        error.value = null

        try {
            const response = await axiosInstance.get(
                `/api/storefront/${encodeURIComponent(slug)}/nav`
            )

            if (myGeneration !== generation) return

            const body = response.data || {}
            const items = Array.isArray(body.nav) ? body.nav : []
            nav.value = items
            navCache.set(slug, items)
            error.value = null
        } catch (err) {
            if (myGeneration !== generation) return
            const status = err?.response?.status ?? 0
            error.value = mapNavError(status)
            nav.value = []
        } finally {
            if (myGeneration === generation) {
                loading.value = false
            }
        }
    }

    return { nav, loading, error, fetchNav }
}

// Explicit invalidation: drops every cached nav AND empties every live
// instance. Called when the server revokes the visitor's store pass (the 403
// `store_gate_required` interceptor branch), on logout
// (storefrontNavCacheLifecycle), and after an editor publish.
export function clearStorefrontNavCache() {
    navCache.clear()
    cacheEpoch.value++
}

function mapNavError(status) {
    if (status === 404) {
        return { status: 404, message: 'This store is no longer available.' }
    }
    return { status, message: 'Failed to load the store menu.' }
}
